(ns hai.web-app.entity-card
  "Shared scaffolding for the entity-card components (mini-apps, connections,
   integrations). Owns the sharing-block, scope glyphs, and the styled pieces
   that appear identically across all three card types."
  (:require
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.users.helper :as users]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Shared layout pieces
;; ---------------------------------------------------------------------------

(o/defstyled icon-frame :div
  {:width "44px" :height "44px"
   :flex-shrink "0"
   :border-radius "11px"
   :background t/--colour-white
   :box-shadow "inset 0 0 0 1px var(--card-ink-06)"
   :display "flex" :align-items "center" :justify-content "center"}
  ;; Force every brand/MUI icon to render at 28×28 inside the frame.
  ;; `display: block` kills the inline-SVG descender space — without it the
  ;; glyph sits on the text baseline and looks a few pixels off-centre.
  ;; `overflow: visible` covers brand icons whose path data extends past
  ;; their declared viewBox (Notion + the JDBC database glyph both do).
  [:svg {:width "28px" :height "28px" :display "block" :overflow "visible"}])

(o/defstyled foot-label :span
  {:font-size "12px" :font-weight "500"
   :color t/--card-text-muted})

;; ---------------------------------------------------------------------------
;; Scope marks — one named piece per kind so a reader can grep by intent.
;; ---------------------------------------------------------------------------

(o/defstyled person-dot :span
  {:width "6px" :height "6px"
   :flex-shrink "0"
   :border-radius "50%"
   :background t/--card-text-faint})

(o/defstyled team-dot :span
  {:width "6px" :height "6px"
   :flex-shrink "0"
   :border-radius "50%"
   :background t/--card-violet})

(o/defstyled everyone-glyph :span
  {:width "18px" :height "18px"
   :flex-shrink "0"
   :border-radius "5px"
   :background t/--card-ink
   :display "inline-flex" :align-items "center" :justify-content "center"
   :color t/--colour-white}
  [:svg {:width "11px" :height "11px"}])

(o/defstyled domain-glyph :span
  {:width "18px" :height "18px"
   :flex-shrink "0"
   :border-radius "5px"
   :background t/--card-violet-soft
   :display "inline-flex" :align-items "center" :justify-content "center"
   :color t/--card-ink-hover}
  [:svg {:width "11px" :height "11px"}])

(defn- scope-mark [kind]
  (case kind
    :everyone [everyone-glyph [icons/outlined-public]]
    :domain   [domain-glyph [icons/outlined-language]]
    :team     [team-dot]
    :person   [person-dot]
    [person-dot]))

;; ---------------------------------------------------------------------------
;; Sharing summary + add-share pill
;; ---------------------------------------------------------------------------

(o/defstyled sharing-summary :div
  {:display "flex" :align-items "center" :gap "8px"
   :min-width "0"
   :border-radius "7px"
   :padding "2px 6px"
   :margin "-2px -6px"
   :transition "background .15s ease"}
  [:&.clickable {:cursor "pointer"}]
  [:&.clickable:hover {:background t/--card-ink-04}])

(o/defstyled sharing-lead :span
  {:flex "1 1 auto" :min-width "0"
   :font-size "13px" :font-weight "500" :color t/--card-ink
   :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"})

(o/defstyled sharing-tail :span
  {:flex-shrink "0"
   :font-size "12px" :color t/--card-text-muted})

(o/defstyled add-share-pill :button
  {:display "inline-flex" :align-items "center"
   :height "24px"
   :padding "0 10px"
   :border-radius "999px"
   :border "1px dashed var(--card-border)"
   :background "transparent"
   :color t/--card-text-muted
   :font-size "12px" :font-weight "500" :font-family "inherit"
   :cursor "pointer"
   :width "fit-content"
   :transition "background .15s ease, border-color .15s ease, color .15s ease"}
  [:&:hover
   {:border "1px solid var(--card-sage)"
    :background t/--card-forest-06
    :color t/--card-ink}])

;; ---------------------------------------------------------------------------
;; Owner summarisation
;; ---------------------------------------------------------------------------

(defn- everyone-scope? [scope]
  (or (= "*" scope) (= "published" scope)))

(defn- domain-scope? [scope]
  (and scope (not (everyone-scope? scope))))

(defn- person-count-label [n]
  (str "+" n " " (if (= 1 n) "person" "people")))

(defn summarize-owners
  "Turns `:entity/shared-with` into `{:kind :label :tail}`. Priority:
   everyone > domain > single > pair > team."
  [owners]
  (let [scopes    (map :entity/scope owners)
        people    (remove :entity/scope owners)
        everyone? (some everyone-scope? scopes)
        domain    (some #(when (domain-scope? %) %) scopes)
        total     (count owners)]
    (cond
      everyone?
      {:kind :everyone
       :label "Everyone"
       :tail (when (> total 1)
               (str "+" (dec total) " other" (when (> total 2) "s")))}

      domain
      {:kind :domain
       :label (str "@" domain)
       :tail (when (seq people) (person-count-label (count people)))}

      (= 1 total)
      {:kind :person
       :label (users/->label (first owners))}

      (= 2 total)
      {:kind :team
       :label (str (users/->label (first owners)) ", " (users/->label (second owners)))}

      :else
      {:kind :team
       :label (users/->label (first owners))
       :tail (when (pos? (dec total)) (str "+" (dec total)))})))

;; ---------------------------------------------------------------------------
;; Sharing block
;; ---------------------------------------------------------------------------

(defn sharing-block
  "Renders the foot-row sharing slot: `Shared with:` label, summary or
   `+ Add` pill, and the Manage-Access dialog. Returns a fragment so the
   caller controls outer wrapping. Renders nothing when there are no
   shares AND the viewer can't share."
  [{:keys [collection entity aria-label dialog-description]}]
  (r/with-let [!open? (r/atom false)]
    (let [shared-with (:entity/shared-with entity)
          can-share?  (sharing/can-share? entity)
          shared?     (boolean (seq shared-with))
          show?       (or shared? can-share?)
          open-dialog (fn [e]
                        (.stopPropagation e)
                        (reset! !open? true))
          {:keys [kind label tail]} (summarize-owners shared-with)]
      (when show?
        [:<>
         [foot-label "Shared with:"]
         (if shared?
           [sharing-summary
            (cond-> {}
              can-share? (assoc :class "clickable"
                                :role "button"
                                :tabIndex 0
                                :on-click open-dialog
                                :on-key-down
                                (fn [e]
                                  (when (#{"Enter" " "} (.-key e))
                                    (.preventDefault e)
                                    (.stopPropagation e)
                                    (reset! !open? true)))))
            (scope-mark kind)
            [sharing-lead label]
            (when tail [sharing-tail tail])]
           [add-share-pill
            {:type "button"
             :aria-label aria-label
             :on-click open-dialog}
            "+ Add"])
         [c/DialogRoot
          {:open @!open?
           :onOpenChange (fn [open?]
                           (reset! !open? open?)
                           (when-not open? (>e [:table/resolve collection])))}
          [c/DialogContent
           [sharing.components/sharing-dialog-title]
           [c/dialog-close-x]
           [c/DialogDescription dialog-description]
           [sharing.components/permissions-table entity]]]]))))
